import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { object, func } from "prop-types";
import get from "lodash/get";
import { logout, updateUserProfile } from "@quintype/bridgekeeper-js";
import { navigateTo } from "../../../arrow/utils/utils";
import { MEMBER_UPDATED } from "../../store/actions";
import { NOTIFICATION_PREFERENCES, MEMBER_DASHBOARD_TABS } from "../../../constants";
import { TabNavigation } from "../../molecules/tab-navigation";
import { ProfileCard } from "../../molecules/profile-card";
import { CommunicationPreferencesModal } from "../../molecules/communication-preferences-modal";
import { CommunicationPreferencesConfirmationModal } from "../../molecules/communication-preferences-confirmation-modal";
import { ProfileEditModal } from "../../molecules/profile-edit-modal";
import { fetchMemberDirectoryData } from "../../../../server/data-loaders/member-directory-data";
import { ProfileTab, EventsTab, BookmarksTab, SurveyTab, MemberDirectoryTab } from "./tabs";
import { ClientManagerSection } from "../../molecules/client-manager-section"
import { BookmarksList } from "../../molecules/bookmarks-list";

import "./member-dashboard.m.css";

export const MemberDashboard = ({
  profileData,
  subscriberMetadata,
  setSubscriberMetadata,
  setImaSubscriberMetadata,
  getSubscriberMetadata,
  ...props
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useDispatch();
  const [communicationPreference, setCommunicationPreference] = useState(get(subscriberMetadata, 'communication-preference') || null);
  const [directorySharing, setDirectorySharing] = useState(get(subscriberMetadata, 'opted-share') || 'no');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [isProfileImageModalOpen, setIsProfileImageModalOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when any modal is open and hide back-to-top button
  useEffect(() => {
    const isAnyModalOpen = showSuccessModal || showConfirmationModal || showProfileEditModal || showResetPasswordModal || isProfileImageModalOpen;
    const backToTopBtn = document.getElementById('backToTopBtn');

    if (isAnyModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      if (backToTopBtn) {
        backToTopBtn.style.display = 'none';
      }
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
      if (backToTopBtn) {
        backToTopBtn.style.display = 'flex';
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('modal-open');
      if (backToTopBtn) {
        backToTopBtn.style.display = 'flex';
      }
    };
  }, [showSuccessModal, showConfirmationModal, showProfileEditModal, showResetPasswordModal, isProfileImageModalOpen]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  // Get AccessType configuration and member info from Redux store
  const { key: accessTypeKey, accessTypeSubAuth } = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "accesstypeConfig"], {})
  );
  const member = useSelector((state) => get(state, ["member"], null));
  const currentUserId = get(member, 'id');


  // Fetch member directory data when directory tab is active
  useEffect(() => {
    if (activeTab === 4 && accessTypeKey && members.length === 0 && !membersLoading) {
      loadMemberDirectoryData();
    }
  }, [activeTab, accessTypeKey]);

  const loadMemberDirectoryData = async () => {
    if (!accessTypeKey) {
      setMembersError("AccessType configuration not found");
      return;
    }

    setMembersLoading(true);
    setMembersError(null);

    try {
      let metadata = {
        'primary-role': get(subscriberMetadata, 'primary-role') || '',
        'opted-share': 'Yes'
      }
      const response = await fetchMemberDirectoryData(accessTypeKey, accessTypeSubAuth, {
        limit: 5000,
        offset: 0,
        metadata,
        currentUserId
      });
      const sortedMembers = (response?.members || []).slice().sort((a, b) => {
        const companyA = (a.company || "").trim();
        const companyB = (b.company || "").trim();
        if (!companyA && !companyB) return 0;
        if (!companyA) return 1;
        if (!companyB) return -1;
        return companyA.localeCompare(companyB, undefined, { sensitivity: "base", numeric: true });
      });
      setMembers(sortedMembers);
      setFilteredMembers(sortedMembers);

      const cities = response?.members
        ?.map(member => member?.location)
        ?.filter(location => location && location.trim() !== '')
        ?.reduce((unique, city) => {
          const trimmedCity = city.trim();
          return unique.includes(trimmedCity) ? unique : [...unique, trimmedCity];
        }, [])
        ?.sort() || [];

      setAvailableCities(cities);
    } catch (error) {
      console.error('Error loading member directory data:', error);
      setMembersError(error.message || 'Failed to load member directory');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleTabChange = (index, tab) => {
    setActiveTab(index);
  };

  const handleLogout = async () => {
    try {
      await logout();
      dispatch({ type: MEMBER_UPDATED, member: null });
      navigateTo(dispatch, "/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handlePreferenceChange = (preference) => {
    setCommunicationPreference(preference);
  };

  const handleDirectorySharing = (opted) => {
    setDirectorySharing(opted);
  }

  const handlePreferenceSave = (preference) => {
    setCommunicationPreference(preference);
    setShowConfirmationModal(true);
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  const handleProfileEdit = () => {
    setShowProfileEditModal(true);
  };

  const handleCloseProfileEditModal = () => {
    setShowProfileEditModal(false);
  };

  const handleImageUpload = async (uploadData) => {

    try {
      // Update subscriber metadata with avatar-url
      if (setSubscriberMetadata) {
        const updatedMetadata = {
          ...subscriberMetadata,
          'profile-image': uploadData ? uploadData.avatarUrl : null
        };

        await setSubscriberMetadata({ other: updatedMetadata });
        setImaSubscriberMetadata(updatedMetadata);
      }
    } catch (error) {
    }
  };

  const handleConfirmPreferences = async (preferencesData) => {
    try {
      // Update the local subscriberMetadata with the new preferences
      const updatedMetadata = {
        ...subscriberMetadata,
        'communication-preference': preferencesData.communicationPreference,
        'opted-share': preferencesData.directorySharing
      };

      // Update user profile in Bridgekeeper with communication preferences
      const userProfileUpdateData = {
        metadata: {
          'communication-preference': preferencesData.communicationPreference,
          'opted-share': preferencesData.directorySharing
        }
      };

      // Call Bridgekeeper's updateUserProfile method
      const userProfileResponse = await updateUserProfile(userProfileUpdateData);

      // Call AccessType's setSubscriberMetadata method
      if (setSubscriberMetadata) {
        await setSubscriberMetadata({ other: updatedMetadata });
      }

      // Update Redux store with updated user profile
      if (userProfileResponse && userProfileResponse.user) {
        dispatch({
          type: MEMBER_UPDATED,
          member: userProfileResponse.user
        });
      }
      setImaSubscriberMetadata(updatedMetadata);
      setShowConfirmationModal(false);
      setShowSuccessModal(true);

    } catch (error) {
      console.error("Error saving preferences:", error);
    }
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const applyFilters = (searchTerm = "", cities = selectedCities) => {
    let filtered = [...members];

    // Apply search filter
    if (searchTerm.trim()) {
      const normalizedSearchTerm = searchTerm.toLowerCase();

      filtered = filtered.filter(member =>
        [member.name, member.company]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedSearchTerm))
      );
    }
    // Apply city filter
    if (cities.length > 0) {
      filtered = filtered.filter(member =>
        member.location && cities.includes(member.location.trim())
      );
    }

    setFilteredMembers(filtered);
  };

  const handleMemberSearch = (searchTerm) => {
    applyFilters(searchTerm);
  };

  const handleCityFilter = (city) => {
    const newSelectedCities = selectedCities.includes(city)
      ? selectedCities.filter(c => c !== city)
      : [...selectedCities, city];

    setSelectedCities(newSelectedCities);
    applyFilters("", newSelectedCities);
  };

  const handleRemoveCityFilter = (city) => {
    const newSelectedCities = selectedCities.filter(c => c !== city);
    setSelectedCities(newSelectedCities);
    applyFilters("", newSelectedCities);
  };

  const handleClearAllFilters = () => {
    setSelectedCities([]);
    applyFilters("", []);
  };

  const clientManagerData = {
    title: "Dedicated Client Manager",
    description: "For any queries related to your membership, please reach out to your assigned manager.",
    name: get(subscriberMetadata, 'client-manager') || '',
    email: get(subscriberMetadata, 'client-manager-email') || '',
    phone: get(subscriberMetadata, 'client-manager-mobile') || ''
  };

  const isMobileMenuOpen = useSelector((state) => get(state, ["isMobileMenuOpen"], false));

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <ProfileTab
            communicationPreference={communicationPreference}
            onPreferenceChange={handlePreferenceChange}
            onPreferenceSave={handlePreferenceSave}
            showResetPasswordModal={showResetPasswordModal}
            onResetPasswordModalOpen={() => setShowResetPasswordModal(true)}
            onResetPasswordModalClose={() => setShowResetPasswordModal(false)}
          />
        );
      case 1:
        return <EventsTab memberData={profileData} />;
      case 2:
        return <BookmarksList />;
      case 3:
        return <SurveyTab userEmail={get(subscriberMetadata, 'email') || get(member, 'email')} />;
      case 4:
        return (
          <MemberDirectoryTab
            filteredMembers={filteredMembers}
            membersLoading={membersLoading}
            membersError={membersError}
            selectedCities={selectedCities}
            availableCities={availableCities}
            onSearchChange={handleMemberSearch}
            onCityFilterChange={handleCityFilter}
            onRetry={loadMemberDirectoryData}
            onRemoveCityFilter={handleRemoveCityFilter}
            onClearAllFilters={handleClearAllFilters}
            primaryRole={subscriberMetadata?.['primary-role']}
          />
        );
      default:
        return <ProfileTab />;
    }
  };

  return (
    <div styleName="member-dashboard" {...props}>
      {/* Banner Section */}
      <div styleName="banner-section">
        <div styleName="banner-content">
          <div styleName="breadcrumb">
            <span styleName="breadcrumb-text">Account</span>
          </div>
          <div styleName="main-heading">
            <h1 styleName="page-title">Member dashboard</h1>
          </div>
        </div>
      </div>

			{/* Tab Navigation */}
			<div styleName="tab-section">
				<div styleName="tab-container">
					<div styleName="desktop-navigation">
						<TabNavigation
							tabs={MEMBER_DASHBOARD_TABS}
							activeTab={activeTab}
							onTabChange={handleTabChange}
						/>
					</div>

					{/* Mobile Title Section */}
					<div styleName="mobile-title-section">
						<div styleName="page-title-section">
							<h1 styleName="current-page-title">
								{MEMBER_DASHBOARD_TABS[activeTab]?.label || "My profile"}
							</h1>
						</div>
					</div>

					<div styleName="sign-out-container">
						<button
							styleName="sign-out-link"
							type="button"
							onClick={handleLogout}
						>
							Sign out
						</button>
					</div>
				</div>
			</div>

			{/* Mobile Tab Navigation - Floating Button */}
			{isMobile && (
				<div
					styleName="mobile-nav-wrapper"
					style={{
						display:
							showSuccessModal ||
							showConfirmationModal ||
							showProfileEditModal ||
							showResetPasswordModal ||
							isProfileImageModalOpen ||
							isMobileMenuOpen
								? "none"
								: "block"
					}}
				>
					<TabNavigation
						tabs={MEMBER_DASHBOARD_TABS}
						activeTab={activeTab}
						onTabChange={handleTabChange}
					/>
				</div>
			)}

      {/* Profile Section */}
      {activeTab === 0 && (
        <ProfileCard
          profileData={profileData}
          onEditClick={handleProfileEdit}
          onImageUpload={handleImageUpload}
          onImageModalStateChange={setIsProfileImageModalOpen}
        />
      )}

			{/* Content Section */}
			<div
				styleName={`content-section ${
					MEMBER_DASHBOARD_TABS[activeTab]?.id === "bookmarks"
						? "is-bookmarks"
						: ""
				}`}
			>
				{renderTabContent()}
			</div>

			{/* Client Manager Section */}
			{activeTab === 0 && (
				<ClientManagerSection managerData={clientManagerData} />
			)}

			{/* Communication Preferences Confirmation Modal */}
			<CommunicationPreferencesConfirmationModal
				isOpen={showConfirmationModal}
				onClose={handleCloseConfirmationModal}
				selectedPreference={communicationPreference}
				onConfirm={handleConfirmPreferences}
				onPreferenceChange={handlePreferenceChange}
				selectedDirectorySharing={directorySharing}
				onDirectorySharingChange={handleDirectorySharing}
				subscriberMetadata={subscriberMetadata}
			/>

			{/* Communication Preferences Success Modal */}
			<CommunicationPreferencesModal
				isOpen={showSuccessModal}
				onClose={handleCloseModal}
				selectedPreference={communicationPreference}
				memberData={profileData}
				subscriberMetadata={subscriberMetadata}
			/>

			{/* Profile Edit Modal */}
			<ProfileEditModal
				isOpen={showProfileEditModal}
				onClose={handleCloseProfileEditModal}
				profileData={profileData}
				subscriberMetadata={subscriberMetadata}
				setSubscriberMetadata={setSubscriberMetadata}
				getSubscriberMetadata={getSubscriberMetadata}
				setImaSubscriberMetadata={setImaSubscriberMetadata}
			/>
		</div>
	);
};

MemberDashboard.propTypes = {
  profileData: object.isRequired,
};